/**
 * TurnstileWidget — Cloudflare Turnstile challenge widget.
 *
 * Track 4.I — Turnstile client integration.
 * Architecture ref: §4.18 — Turnstile required on signup, password-reset-request,
 *   and login after TURNSTILE_THRESHOLD consecutive failures.
 *
 * Behaviour:
 *   - Loads the Turnstile JS API script (defer) on first mount.
 *   - Renders the widget inside a div with the configured sitekey.
 *   - Calls `onVerify(token)` when the user passes the challenge.
 *   - Calls `onError()` when the challenge fails or expires (caller should
 *     reset the form's Turnstile token state so submission is blocked again).
 *   - Exposes `reset()` via ref for the parent form to reset the widget after
 *     a failed form submission (the token is single-use).
 *
 * Sitekey source: `import.meta.env.VITE_TURNSTILE_SITE_KEY`
 *   - Set in `apps/web/.env` for dev, and in CF Pages env vars for prod.
 *   - An absent sitekey renders a disabled placeholder (dev-friendy; never
 *     reached in production because Pages build fails on a missing required var).
 *
 * Mobile-friendliness (Rule 7):
 *   - The Turnstile widget is responsive by default (CF renders the compact
 *     or normal variant based on available width).
 *   - The container uses `width: 100%; max-width: 300px` to prevent overflow
 *     on narrow screens while staying centred on larger ones.
 *   - `appearance: "interaction-only"` is set on mobile viewports to minimise
 *     layout shift from the checkbox widget.
 *
 * Voice note: error state uses precise, unsentimental copy per branding.md §5.
 * No "Oops", no exclamations, no emoji.
 */

import { useEffect, useRef, useImperativeHandle, forwardRef } from "react";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TurnstileWidgetRef {
  /** Reset the widget (clears the current token; the user must solve again). */
  reset(): void;
}

export interface TurnstileWidgetProps {
  /**
   * Called when the user passes the Turnstile challenge.
   * The `token` parameter is the one-time-use token to submit to the server.
   */
  onVerify(token: string): void;
  /**
   * Called when the challenge errors or the token expires.
   * The parent should reset its `turnstileToken` state to "" to block submission.
   */
  onError?(): void;
  /** Optional additional CSS class name for the container div. */
  className?: string;
}

// ── Turnstile JS API types ────────────────────────────────────────────────────

interface TurnstileRenderParams {
  sitekey: string;
  callback: (token: string) => void;
  "error-callback": (() => void) | undefined;
  "expired-callback": (() => void) | undefined;
  appearance?: "always" | "execute" | "interaction-only";
  theme?: "auto" | "light" | "dark";
  size?: "normal" | "compact";
}

interface TurnstileAPI {
  render(container: HTMLElement, params: TurnstileRenderParams): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

// Cloudflare Turnstile attaches to `window.turnstile`.
declare global {
  interface Window {
    turnstile?: TurnstileAPI;
    onTurnstileLoad?: () => void;
  }
}

// ── Script loader (idempotent) ────────────────────────────────────────────────

const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad";

let scriptLoaded = false;
const pendingCallbacks: Array<() => void> = [];

/**
 * Load the Turnstile script once and call `cb` when ready.
 * Subsequent calls after the script is loaded call `cb` synchronously.
 */
function loadTurnstileScript(cb: () => void): void {
  if (scriptLoaded && window.turnstile) {
    cb();
    return;
  }

  pendingCallbacks.push(cb);

  if (document.querySelector(`script[src^="${TURNSTILE_SCRIPT_SRC.split("?")[0]}"]`)) {
    // Script tag already in DOM — already loading, pending callbacks will fire
    // via onTurnstileLoad.
    return;
  }

  // Install the global load callback before inserting the script tag.
  window.onTurnstileLoad = () => {
    scriptLoaded = true;
    const cbs = pendingCallbacks.splice(0);
    for (const fn of cbs) fn();
  };

  const script = document.createElement("script");
  script.src = TURNSTILE_SCRIPT_SRC;
  script.defer = true;
  script.async = true;
  document.head.appendChild(script);
}

// ── Component ─────────────────────────────────────────────────────────────────

const SITEKEY = import.meta.env["VITE_TURNSTILE_SITE_KEY"] as string | undefined;

/**
 * Renders a Cloudflare Turnstile widget.
 *
 * Forward-ref exposes `reset()` so the parent form can reset the widget after
 * a failed submission attempt (the one-time token must be refreshed before retry).
 *
 * @example
 * ```tsx
 * const widgetRef = useRef<TurnstileWidgetRef>(null);
 * const [tsToken, setTsToken] = useState("");
 *
 * // After failed form submit:
 * widgetRef.current?.reset();
 * setTsToken("");
 *
 * <TurnstileWidget
 *   ref={widgetRef}
 *   onVerify={setTsToken}
 *   onError={() => setTsToken("")}
 * />
 * ```
 */
export const TurnstileWidget = forwardRef<TurnstileWidgetRef, TurnstileWidgetProps>(
  function TurnstileWidget({ onVerify, onError, className }, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);

    // Expose reset() to parent via ref.
    useImperativeHandle(ref, () => ({
      reset() {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }));

    useEffect(() => {
      if (!SITEKEY) {
        // Dev: sitekey not configured — widget is inert.
        return;
      }

      const container = containerRef.current;
      if (!container) return;

      let widgetId: string | null = null;

      loadTurnstileScript(() => {
        if (!window.turnstile || !container) return;

        widgetId = window.turnstile.render(container, {
          sitekey: SITEKEY,
          callback: onVerify,
          "error-callback": onError,
          "expired-callback": onError,
          theme: "auto",
          // Use compact size on narrow screens to avoid horizontal overflow.
          size: "normal",
        });
        widgetIdRef.current = widgetId;
      });

      return () => {
        if (widgetId && window.turnstile) {
          window.turnstile.remove(widgetId);
          widgetIdRef.current = null;
        }
      };
      // onVerify and onError are callbacks — they should be stable refs from
      // the parent (useCallback). Re-rendering with new function references
      // would cause the widget to remount unnecessarily. Deps intentionally
      // empty: the effect must run once on mount only.
    }, []);

    if (!SITEKEY) {
      // Dev placeholder: sitekey not set.
      return (
        <div
          className={className}
          style={{
            width: "100%",
            maxWidth: 300,
            padding: "12px",
            border: "1px dashed var(--border, #D9D4CC)",
            borderRadius: 3,
            fontSize: 12,
            color: "var(--fg-muted, #7A756E)",
            fontFamily: "inherit",
            boxSizing: "border-box",
          }}
          aria-hidden="true"
        >
          Turnstile widget (VITE_TURNSTILE_SITE_KEY not set)
        </div>
      );
    }

    return (
      <div
        ref={containerRef}
        className={className}
        style={{ width: "100%", maxWidth: 300 }}
        // The Turnstile iframe carries its own ARIA role. The container
        // div is purely structural.
        aria-hidden={false}
      />
    );
  },
);
